import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  Container,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  TextField,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem
} from "@mui/material";
import {
  Edit,
  Save,
  AddPhotoAlternate,
  Add,
  Close,
  Delete,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import apiEndpoints from "../../apiconfig";
import OurEventsSlider from "../OurEventsSlider";

// Styled Components (remains the same)
const GradientCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
  },
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  margin: "8px 0 6px",
  backgroundColor: theme.palette.grey[200],
  "& .MuiLinearProgress-bar": {
    borderRadius: 5,
    backgroundColor: "#00b894",
  },
}));

const StyledTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    height: 4,
    borderRadius: "2px 2px 0 0",
    backgroundColor: "#00b894",
  },
  marginBottom: "24px",
  "& .Mui-selected": {
    color: "#00b894 !important",
  },
});

const ContentImage = styled(CardMedia)({
  height: 120,
  borderRadius: "10px",
  marginTop: "8px",
  marginBottom: "8px",
  backgroundSize: "cover",
  backgroundPosition: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
});

const ReadMoreButton = styled(Button)({
  textTransform: "none",
  padding: 0,
  minWidth: "auto",
  marginLeft: "4px",
  color: "#00b894",
  fontWeight: 600,
  fontSize: "0.875rem",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
});

const ImagePreview = styled("img")({
  width: "100%",
  maxHeight: "120px",
  borderRadius: "10px",
  marginTop: "6px",
  marginBottom: "6px",
  display: "block",
  objectFit: "cover",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
});

const FundingBox = styled(Box)({
  backgroundColor: "rgba(0, 184, 148, 0.05)",
  borderRadius: "10px",
  padding: "10px",
  margin: "10px 0",
  border: "1px solid rgba(0, 184, 148, 0.1)",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "& fieldset": {
      borderColor: "rgba(0, 184, 148, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 184, 148, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00b894",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.6)",
    "&.Mui-focused": {
      color: "#00b894",
    },
  },
});

const ImageUrlField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "rgba(248, 250, 252, 0.9)",
    fontSize: "0.875rem",
    "& fieldset": {
      borderColor: "rgba(0, 184, 148, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 184, 148, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00b894",
    },
  },
});

const AddImageCard = styled(Card)({
  borderRadius: "16px",
  height: "240px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: "2px dashed #00b894",
  color: "#00b894",
  background: "rgba(0,184,148,0.04)",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#009975",
    backgroundColor: "rgba(0,184,148,0.08)",
  },
});

const GalleryTab = styled(Button)({
  padding: "12px 20px",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.9rem",
  fontFamily: '"Montserrat", sans-serif',
  transition: "all 0.2s ease",
  backgroundColor: "transparent",
  color: "#64748b",
  "&.active": {
    backgroundColor: "#00b894",
    color: "white",
    boxShadow: "0 2px 8px rgba(0,184,148,0.3)",
  },
  "&:hover:not(.active)": {
    backgroundColor: "#f1f5f9",
    color: "#374151",
  },
});

// Main Component
const CMSPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeGalleryTab, setActiveGalleryTab] = useState(0);
  const [expandedFields, setExpandedFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTabName, setNewTabName] = useState("");
  const [showAddTabInput, setShowAddTabInput] = useState(false);
  const [causes, setCauses] = useState([]);
  const [galleryData, setGalleryData] = useState({
    title: "Our Gallery",
    loading: true,
    error: null,
    causes: [],
  });
  const [galleryTabKeys, setGalleryTabKeys] = useState([
    "Homeless",
    "Clean Water",
    "Education",
    "Food and Health",
  ]);
  const [showAddImageDialog, setShowAddImageDialog] = useState(false);
  const [newGalleryImageUrl, setNewGalleryImageUrl] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tabToDelete, setTabToDelete] = useState(null);
  const [addCauseModalOpen, setAddCauseModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCause, setEditingCause] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [newCause, setNewCause] = useState({
    title: "New Cause",
    description: "",
    content1: "",
    content2: "",
    imageMain: null,
    imageContent1: null,
    imageContent2: null,
    imageContent3: null
  });

  const [newCausePreviews, setNewCausePreviews] = useState({
    imageMain: '',
    imageContent1: '',
    imageContent2: '',
    imageContent3: ''
  });

  // Use ref to track object URLs for cleanup
  const objectUrlsRef = useRef({});

  // Cleanup function for object URLs
  useEffect(() => {
    return () => {
      // Clean up all object URLs when component unmounts
      Object.values(objectUrlsRef.current).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  // Edit Cause Modal Component
  const EditCauseModal = ({ open, onClose, cause, onSave, loading }) => {
    const [editedCause, setEditedCause] = useState(cause || {});
    const [imagePreviews, setImagePreviews] = useState({
      imageMain: '',
      imageContent1: '',
      imageContent2: '',
      imageContent3: ''
    });

    // Initialize previews when cause changes
    useEffect(() => {
      if (cause) {
        const previews = {
          imageMain: cause.imageMain || '',
          imageContent1: cause.imageContent1 || '',
          imageContent2: cause.imageContent2 || '',
          imageContent3: cause.imageContent3 || ''
        };
        setImagePreviews(previews);
      }
    }, [cause]);

    const handleChange = (field, value) => {
      setEditedCause(prev => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (field, e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Revoke previous object URL if exists
      if (objectUrlsRef.current[`${cause?.id}-${field}`]) {
        URL.revokeObjectURL(objectUrlsRef.current[`${cause?.id}-${field}`]);
      }

      // Create new preview URL
      const previewUrl = URL.createObjectURL(file);
      objectUrlsRef.current[`${cause?.id}-${field}`] = previewUrl;

      setImagePreviews(prev => ({ ...prev, [field]: previewUrl }));
      setEditedCause(prev => ({ ...prev, [field]: file }));
    };

    const handleSave = () => {
      // Create FormData for file upload
      const formData = new FormData();

      formData.append('_method', 'PUT');
      formData.append('id', editedCause.id);
      formData.append('mainHeader', editedCause.title);
      formData.append('maincontent', editedCause.description);
      formData.append('pagecontent', editedCause.content1);
      formData.append('pagecontent_1', editedCause.content2);

      // Append files if they exist and are new (File objects)
      if (editedCause.imageMain instanceof File) {
        formData.append('image_url', editedCause.imageMain);
      }
      if (editedCause.imageContent1 instanceof File) {
        formData.append('img1', editedCause.imageContent1);
      }
      if (editedCause.imageContent2 instanceof File) {
        formData.append('img2', editedCause.imageContent2);
      }

      onSave(formData);
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth={"md"}>
        <DialogTitle>Edit Cause</DialogTitle>
        <DialogContent>
          {editedCause && (
            <Grid container spacing={2} sx={{ mt: 1, maxHeight:"300px"}}>
                <StyledTextField
                  label="Title"
                  fullWidth
                  value={editedCause.title || ''}
                  onChange={(e) => handleChange("title", e.target.value)}
                  sx={{ mb: 2 ,}}
                />

                <StyledTextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={editedCause.description || ''}
                  onChange={(e) => handleChange("description", e.target.value)}
                  sx={{ mb: 2 }}
                />
             

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="main-image-upload"
                  type="file"
                  onChange={(e) => handleFileChange("imageMain", e)}
                />
                <label htmlFor="main-image-upload">
                  <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                    Upload Main Image
                  </Button>
                </label>
                {imagePreviews.imageMain && (
                  <ImagePreview src={imagePreviews.imageMain} alt="Main Preview" />
                )}

                <StyledTextField
                  label="Content Section 1"
                  fullWidth
                  multiline
                  rows={3}
                  value={editedCause.content1 || ''}
                  onChange={(e) => handleChange("content1", e.target.value)}
                  sx={{ mb: 2 }}
                />

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="content-image-1-upload"
                  type="file"
                  onChange={(e) => handleFileChange("imageContent1", e)}
                />
                <label htmlFor="content-image-1-upload">
                  <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                    Upload Content Image 1
                  </Button>
                </label>
                {imagePreviews.imageContent1 && (
                  <ImagePreview src={imagePreviews.imageContent1} alt="Content 1 Preview" />
                )}

                <StyledTextField
                  label="Content Section 2"
                  fullWidth
                  multiline
                  rows={3}
                  value={editedCause.content2 || ''}
                  onChange={(e) => handleChange("content2", e.target.value)}
                  sx={{ mb: 2 }}
                />

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="content-image-2-upload"
                  type="file"
                  onChange={(e) => handleFileChange("imageContent2", e)}
                />
                <label htmlFor="content-image-2-upload">
                  <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                    Upload Content Image 2
                  </Button>
                </label>
                {imagePreviews.imageContent2 && (
                  <ImagePreview src={imagePreviews.imageContent2} alt="Content 2 Preview" />
                )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "#00b894",
              "&:hover": { backgroundColor: "#009975" },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };


  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  // Fetch causes data
  useEffect(() => {
    const fetchCauses = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiEndpoints.oruCauses);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Map API data to match your component structure
        const formattedCauses = data.map((cause) => ({
          id: cause.id,
          title: cause.mainHeader,
          description: cause.maincontent,
          content1: cause.pagecontent,
          content2: cause.pagecontent_1,
          imageMain: cause.image_url ? `${apiEndpoints.idproof_path}${cause.image_url}` : '',
          imageContent1: cause.img1 ? `${apiEndpoints.idproof_path}${cause.img1}` : '',
          imageContent2: cause.img2 ? `${apiEndpoints.idproof_path}${cause.img2}` : '',
          imageContent3: cause.img3 ? `${apiEndpoints.idproof_path}${cause.img3}` : '',
        }));

        setCauses(formattedCauses);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch causes:", err);
        setError("Failed to load causes. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCauses();
  }, []);

  // Fetch gallery data
  useEffect(() => {
    fetchGalleryData();
  }, []);

  const fetchGalleryData = async () => {
    try {
      setGalleryData((prev) => ({ ...prev, loading: true }));
      const response = await fetch(apiEndpoints.gallery, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract unique cause names for tabs
      const uniqueCauses = [...new Set(data.map((item) => item.causeName))];
      if (uniqueCauses.length > 0) {
        setGalleryTabKeys(uniqueCauses);
      }

      setGalleryData((prev) => ({
        ...prev,
        causes: data,
        loading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching gallery data:", error);
      setGalleryData((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  const deleteCause = async (causeId) => {
    try {
      setLoading(true);
      const response = await fetch(apiEndpoints.oruCauses, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `id=${encodeURIComponent(causeId)}`,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setCauses(causes.filter((cause) => cause.id !== causeId));
    } catch (err) {
      console.error("Failed to delete cause:", err);
      setError("Failed to delete cause. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const truncateText = (text, maxLength) => {
    if (typeof text !== "string") return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const toggleExpandField = (causeId, field) => {
    setExpandedFields((prev) => ({
      ...prev,
      [`${causeId}-${field}`]: !prev[`${causeId}-${field}`],
    }));
  };

  const isFieldExpanded = (causeId, field) => {
    return expandedFields[`${causeId}-${field}`];
  };

  // Add new gallery tab
  const handleAddTab = async () => {
    if (!newTabName.trim() || galleryTabKeys.includes(newTabName.trim())) return;

    try {
      setGalleryData((prev) => ({ ...prev, loading: true }));

      // Create new gallery item in the backend
      const response = await fetch(apiEndpoints.gallery, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          causeName: newTabName.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newGalleryItem = await response.json();

      setNewTabName("");
      setShowAddTabInput(false);
      fetchGalleryData();
    } catch (err) {
      console.error("Error adding new tab:", err);
      setGalleryData((prev) => ({
        ...prev,
        error: "Failed to add new tab. Please try again.",
      }));
    } finally {
      setGalleryData((prev) => ({ ...prev, loading: false }));
    }
  };

  // Delete gallery tab
  const handleDeleteTab = async (tabIndex) => {
    if (galleryTabKeys.length <= 1) return;

    try {
      setGalleryData((prev) => ({ ...prev, loading: true }));
      const tabName = galleryTabKeys[tabIndex];
      const galleryItem = galleryData.causes.find(
        (item) => item.causeName === tabName
      );

      if (!galleryItem) return;

      // Delete from backend
      const response = await fetch(`${apiEndpoints.gallery}?id=${galleryItem.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state
      const newTabKeys = galleryTabKeys.filter((_, index) => index !== tabIndex);
      setGalleryTabKeys(newTabKeys);

      const newGalleryData = galleryData.causes.filter(
        (item) => item.causeName !== tabName
      );
      setGalleryData((prev) => ({
        ...prev,
        causes: newGalleryData,
      }));

      // Adjust active tab if necessary
      if (activeGalleryTab >= newTabKeys.length) {
        setActiveGalleryTab(Math.max(0, newTabKeys.length - 1));
      }
    } catch (err) {
      console.error("Error deleting tab:", err);
      setGalleryData((prev) => ({
        ...prev,
        error: "Failed to delete tab. Please try again.",
      }));
    } finally {
      setGalleryData((prev) => ({ ...prev, loading: false }));
      setDeleteDialogOpen(false);
    }
  };

  const handleNewCauseFileChange = (field, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke previous object URL if exists
    if (objectUrlsRef.current[`new-${field}`]) {
      URL.revokeObjectURL(objectUrlsRef.current[`new-${field}`]);
    }

    // Create new preview URL
    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current[`new-${field}`] = previewUrl;

    setNewCausePreviews(prev => ({ ...prev, [field]: previewUrl }));
    setNewCause(prev => ({ ...prev, [field]: file }));
  };

  const handleAddCause = async () => {
    try {
      setLoading(true);

      // Create FormData
      const formData = new FormData();

      // Append all fields
      formData.append('mainHeader', newCause.title);
      formData.append('maincontent', newCause.description);
      formData.append('pagecontent', newCause.content1);
      formData.append('pagecontent_1', newCause.content2);

      // Append files if they exist
      if (newCause.imageMain) {
        formData.append('image_url', newCause.imageMain);
      }
      if (newCause.imageContent1) {
        formData.append('img1', newCause.imageContent1);
      }
      if (newCause.imageContent2) {
        formData.append('img2', newCause.imageContent2);
      }
      if (newCause.imageContent3) {
        formData.append('img3', newCause.imageContent3);
      }

      // Send POST request with FormData
      const response = await fetch(apiEndpoints.oruCauses, {
        method: "POST",
        body: formData,
      });

      debugger;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdCause = await response.json();

      // Reset form
      setNewCause({
        title: "New Cause",
        description: "",
        content1: "",
        content2: "",
        imageMain: null,
        imageContent1: null,
        imageContent2: null,
        imageContent3: null
      });

      setNewCausePreviews({
        imageMain: '',
        imageContent1: '',
        imageContent2: '',
        imageContent3: ''
      });

      setAddCauseModalOpen(false);

      // Refresh causes list
      const fetchResponse = await fetch(apiEndpoints.oruCauses);
      const data = await fetchResponse.json();
      const formattedCauses = data.map((cause) => ({
        id: cause.id,
        title: cause.mainHeader,
        description: cause.maincontent,
        content1: cause.pagecontent,
        content2: cause.pagecontent_1,
        imageMain: cause.image_url ? `${apiEndpoints.idproof_path}${cause.image_url}` : '',
        imageContent1: cause.img1 ? `${apiEndpoints.idproof_path}${cause.img1}` : '',
        imageContent2: cause.img2 ? `${apiEndpoints.idproof_path}${cause.img2}` : '',
        imageContent3: cause.img3 ? `${apiEndpoints.idproof_path}${cause.img3}` : '',
      }));
      setCauses(formattedCauses);
    } catch (err) {
      console.error("Failed to add new cause:", err);
      setError("Failed to add new cause. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete gallery image
  const handleDeleteImage = async (galleryItem, imageKey) => {
    if (!galleryItem) return;

    try {
      setGalleryData(prev => ({ ...prev, loading: true }));

      const response = await fetch(`${apiEndpoints.gallery}?id=${galleryItem.id}&column=${imageKey}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...galleryItem,
          [imageKey]: null // Clear the specific slot
        })
      });

      if (!response.ok) throw new Error('Failed to delete image');

      // Update local state - set the specific image to null
      setGalleryData(prev => ({
        ...prev,
        causes: prev.causes.map(item =>
          item.id === galleryItem.id
            ? { ...item, [imageKey]: null }
            : item
        )
      }));
    } catch (err) {
      console.error('Error deleting image:', err);
      setGalleryData(prev => ({
        ...prev,
        error: 'Failed to delete image. Please try again.'
      }));
    } finally {
      setGalleryData(prev => ({ ...prev, loading: false }));
    }
  };

  const handleImageUpload = async (file, slotNumber) => {
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const tabName = galleryTabKeys[activeGalleryTab];
      let galleryItem = galleryData.causes.find(item => item.causeName === tabName);

      // If no gallery item exists for this tab, create one
      if (!galleryItem) {
        const formData = new FormData();
        formData.append('causeName', tabName);

        // Create the gallery item first
        const createResponse = await fetch(apiEndpoints.gallery, {
          method: 'POST',
          body: formData,
        });

        if (!createResponse.ok) throw new Error(`HTTP error! status: ${createResponse.status}`);

        // Refresh to get the new item
        await fetchGalleryData();
        galleryItem = galleryData.causes.find(item => item.causeName === tabName);
        if (!galleryItem) throw new Error('Failed to create gallery item');
      }

      // Update the specific gallery slot
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('id', galleryItem.id);
      formData.append(`gallery${slotNumber}`, file);

      const response = await fetch(`${apiEndpoints.gallery}?id=${galleryItem.id}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      // Refresh gallery data to get the latest image URLs from backend
      await fetchGalleryData();

    } catch (err) {
      console.error('Error uploading image:', err);
      setGalleryData(prev => ({
        ...prev,
        error: 'Failed to upload image. Please try again.'
      }));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  const saveEditedCause = async (formData) => {
    try {
      setLoading(true);

      // Get the ID from formData
      const id = formData.get('id');

      // Send PUT request with FormData
      const response = await fetch(`${apiEndpoints.oruCauses}?id=${id}`, {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Refresh causes list after editing
      const fetchResponse = await fetch(apiEndpoints.oruCauses);
      const data = await fetchResponse.json();
      const formattedCauses = data.map((cause) => ({
        id: cause.id,
        title: cause.mainHeader,
        description: cause.maincontent,
        content1: cause.pagecontent,
        content2: cause.pagecontent_1,
        imageMain: cause.image_url ? `${apiEndpoints.idproof_path}${cause.image_url}` : '',
        imageContent1: cause.img1 ? `${apiEndpoints.idproof_path}${cause.img1}` : '',
        imageContent2: cause.img2 ? `${apiEndpoints.idproof_path}${cause.img2}` : '',
        imageContent3: cause.img3 ? `${apiEndpoints.idproof_path}${cause.img3}` : '',
      }));
      setCauses(formattedCauses);

      setEditModalOpen(false);
    } catch (err) {
      console.error("Error saving cause:", err);
      setError(err.message || "Failed to update cause. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Render Causes Tab
  const renderCausesTab = () => (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 5,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
          Our Causes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddCauseModalOpen(true)}
          sx={{
            backgroundColor: "#00b894",
            "&:hover": {
              backgroundColor: "#009975",
            },
          }}
        >
          Add Cause
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ color: "error.main", textAlign: "center", py: 4 }}>
          {error}
        </Box>
      )}

      <Grid container spacing={4}>
        {causes.map((cause) => {
          // const progress = Math.round((cause.raised / cause.goal) * 100);

          return (
            <Grid item xs={12} sm={6} md={4} lg={4} key={cause.id} width={550}>
              <GradientCard>
                {/* Card Media */}
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={cause.imageMain}
                    alt={cause.title}
                  />
                  <IconButton
                    onClick={() => {
                      setEditingCause(cause);
                      setEditModalOpen(true);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: '#00b894',
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this cause?")) {
                        deleteCause(cause.id);
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      top: 50,
                      right: 10,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: '#ef4444',
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>


                <CardContent
                  sx={{
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: "#2d3748",
                      lineHeight: 1.3,
                      fontSize: "1.1rem",
                      flexShrink: 0,
                    }}
                  >
                    {cause.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      lineHeight: 1.5,
                      flexShrink: 0,
                      fontSize: "0.875rem",
                    }}
                  >
                    {isFieldExpanded(cause.id, "description")
                      ? cause.description
                      : truncateText(cause.description, 100)}
                    {typeof cause.description === "string" &&
                      cause.description.length > 100 && (
                        <ReadMoreButton
                          onClick={() =>
                            toggleExpandField(cause.id, "description")
                          }
                        >
                          {isFieldExpanded(cause.id, "description")
                            ? "Show less"
                            : "Read more"}
                        </ReadMoreButton>
                      )}
                  </Typography>

                  {/* Content Sections */}
                  <Box
                    sx={{
                      mt: 1,
                      flexGrow: 1,
                      overflow: "visible",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          lineHeight: 1.5,
                          color: "#4a5568",
                          fontSize: "0.875rem",
                        }}
                      >
                        {isFieldExpanded(cause.id, "content1")
                          ? cause.content1
                          : truncateText(cause.content1, 80)}
                        {typeof cause.content1 === "string" &&
                          cause.content1.length > 80 && (
                            <ReadMoreButton
                              onClick={() =>
                                toggleExpandField(cause.id, "content1")
                              }
                            >
                              {isFieldExpanded(cause.id, "content1")
                                ? "Less"
                                : "More"}
                            </ReadMoreButton>
                          )}
                      </Typography>
                      {cause.imageContent1 && (
                        <ContentImage
                          image={cause.imageContent1}
                          title="Content image 1"
                        />
                      )}

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          lineHeight: 1.5,
                          color: "#4a5568",
                          fontSize: "0.875rem",
                        }}
                      >
                        {isFieldExpanded(cause.id, "content2")
                          ? cause.content2
                          : truncateText(cause.content2, 80)}
                        {typeof cause.content2 === "string" &&
                          cause.content2.length > 80 && (
                            <ReadMoreButton
                              onClick={() =>
                                toggleExpandField(cause.id, "content2")
                              }
                            >
                              {isFieldExpanded(cause.id, "content2")
                                ? "Less"
                                : "More"}
                            </ReadMoreButton>
                          )}
                      </Typography>
                      {cause.imageContent2 && (
                        <ContentImage
                          image={cause.imageContent2}
                          title="Content image 2"
                        />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </GradientCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Cause Modal */}
      <Dialog
        open={addCauseModalOpen}
        onClose={() => setAddCauseModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Cause</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1, maxHeight:"300px" }}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                label="Title"
                fullWidth
                value={newCause.title}
                onChange={(e) => setNewCause({ ...newCause, title: e.target.value })}
                sx={{ mb: 2 }}
              />

              <StyledTextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newCause.description}
                onChange={(e) => setNewCause({ ...newCause, description: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="new-main-image-upload"
                type="file"
                onChange={(e) => handleNewCauseFileChange("imageMain", e)}
              />
              <label htmlFor="new-main-image-upload">
                <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                  Upload Main Image
                </Button>
              </label>
              {newCausePreviews.imageMain && (
                <ImagePreview src={newCausePreviews.imageMain} alt="Main Preview" />
              )}

              <StyledTextField
                label="Content Section 1"
                fullWidth
                multiline
                rows={3}
                value={newCause.content1}
                onChange={(e) => setNewCause({ ...newCause, content1: e.target.value })}
                sx={{ mb: 2 }}
              />

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="new-content-image-1-upload"
                type="file"
                onChange={(e) => handleNewCauseFileChange("imageContent1", e)}
              />
              <label htmlFor="new-content-image-1-upload">
                <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                  Upload Content Image 1
                </Button>
              </label>
              {newCausePreviews.imageContent1 && (
                <ImagePreview src={newCausePreviews.imageContent1} alt="Content 1 Preview" />
              )}

              <StyledTextField
                label="Content Section 2"
                fullWidth
                multiline
                rows={3}
                value={newCause.content2}
                onChange={(e) => setNewCause({ ...newCause, content2: e.target.value })}
                sx={{ mb: 2 }}
              />

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="new-content-image-2-upload"
                type="file"
                onChange={(e) => handleNewCauseFileChange("imageContent2", e)}
              />
              <label htmlFor="new-content-image-2-upload">
                <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                  Upload Content Image 2
                </Button>
              </label>
              {newCausePreviews.imageContent2 && (
                <ImagePreview src={newCausePreviews.imageContent2} alt="Content 2 Preview" />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddCauseModalOpen(false)}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddCause}
            variant="contained"
            sx={{
              backgroundColor: "#00b894",
              "&:hover": { backgroundColor: "#009975" },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save Cause"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  // Gallery Tab Component
  const renderGalleryTab = () => {
    // Get current gallery images for active tab
    const getCurrentGalleryImages = () => {
      const tabName = galleryTabKeys[activeGalleryTab];
      const galleryItem = galleryData.causes.find(
        (item) => item.causeName === tabName
      );

      // Create array with 6 slots
      const images = Array(6).fill(null).map((_, i) => {
        const key = `gallery${i + 1}`;
        return {
          key,
          url: galleryItem && galleryItem[key]
            ? `${apiEndpoints.idproof_path}${galleryItem[key]}`
            : null
        };
      });

      return images;
    };

    // Check if we can add more images to current tab
    const canAddMoreImages = () => {
      const tabName = galleryTabKeys[activeGalleryTab];
      const galleryItem = galleryData.causes.find(
        (item) => item.causeName === tabName
      );

      if (!galleryItem) return false;

      const galleryKeys = ['gallery1', 'gallery2', 'gallery3', 'gallery4', 'gallery5', 'gallery6'];
      return galleryKeys.some((key) => !galleryItem[key]);
    };

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
            {galleryData.title}
          </Typography>
        </Box>

        {galleryData.loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : galleryData.error ? (
          <Box sx={{ color: "error.main", textAlign: "center", py: 4 }}>
            Error loading gallery: {galleryData.error}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mb: 3,
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "6px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              {galleryTabKeys.map((tab, index) => (
                <GalleryTab
                  key={index}
                  className={activeGalleryTab === index ? "active" : ""}
                  onClick={() => setActiveGalleryTab(index)}
                >
                  {tab}
                  {galleryTabKeys.length > 1 && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setTabToDelete(index);
                        setDeleteDialogOpen(true);
                      }}
                      size="small"
                      sx={{
                        ml: 0.5,
                        color: activeGalleryTab === index ? "white" : "inherit",
                        "&:hover": {
                          backgroundColor: activeGalleryTab === index
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                </GalleryTab>
              ))}

              {showAddTabInput ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 12px",
                  }}
                >
                  <TextField
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddTab()}
                    placeholder="Tab name"
                    autoFocus
                    size="small"
                    sx={{
                      width: "120px",
                      "& .MuiOutlinedInput-root": {
                        height: "36px",
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleAddTab}
                    disabled={!newTabName.trim()}
                    color="primary"
                    sx={{
                      backgroundColor: "#00b894",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#009975",
                      },
                      "&:disabled": {
                        backgroundColor: "#e0e0e0",
                      },
                    }}
                  >
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setShowAddTabInput(false);
                      setNewTabName("");
                    }}
                    sx={{
                      backgroundColor: "#ef4444",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#dc2626",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <GalleryTab
                  onClick={() => setShowAddTabInput(true)}
                  sx={{
                    backgroundColor: "#f1f5f9",
                    color: "#00b894",
                  }}
                >
                  <Add fontSize="small" sx={{ mr: 0.5 }} />
                  Add Tab
                </GalleryTab>
              )}
            </Box>

            <Grid container spacing={3}>
              {getCurrentGalleryImages().map((image, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  {image.url ? (
                    <Card sx={{ /* your card styles */ }}>
                      <CardMedia
                        component="img"
                        height="240"
                        image={image.url}
                        alt={`Gallery ${i + 1}`}
                        sx={{ objectFit: "cover" }}
                      />
                      <Box sx={{ /* delete button styles */ }}>
                        <IconButton
                          onClick={async () => {
                            if (window.confirm("Are you sure you want to delete this image?")) {
                              const tabName = galleryTabKeys[activeGalleryTab];
                              const galleryItem = galleryData.causes.find(
                                (item) => item.causeName === tabName
                              );
                              await handleDeleteImage(galleryItem, image.key);
                            }
                          }}
                          sx={{ color: "#ef4444" }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  ) : (
                    <Box>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id={`gallery-upload-${i}`}
                        type="file"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            await handleImageUpload(file, i + 1); // Pass the gallery slot number
                          } catch (err) {
                            alert(err.message || 'Failed to upload image');
                          }
                        }}
                      />
                      <label htmlFor={`gallery-upload-${i}`}>
                        <AddImageCard>
                          <AddPhotoAlternate sx={{ fontSize: "48px", mb: 1 }} />
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                            Add Image {i + 1}
                          </Typography>
                        </AddImageCard>
                      </label>
                    </Box>
                  )}
                </Grid>
              ))}

              {/* Add Image Card */}
              {canAddMoreImages() && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="gallery-image-upload"
                    type="file"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      try {
                        await handleImageUpload(file);
                      } catch (err) {
                        alert(err.message || 'Failed to upload image');
                      }
                    }}
                  />
                  <label htmlFor="gallery-image-upload">
                    <AddImageCard>
                      {uploading ? (
                        <>
                          <CircularProgress size={48} sx={{ mb: 1 }} />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Uploading... {uploadProgress}%
                          </Typography>
                        </>
                      ) : (
                        <>
                          <AddPhotoAlternate sx={{ fontSize: "48px", mb: 1 }} />
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: "1rem" }}
                          >
                            Add Image
                          </Typography>
                        </>
                      )}
                    </AddImageCard>
                  </label>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Delete Tab Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Tab</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the "{galleryTabKeys[tabToDelete]}" tab?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteTab(tabToDelete)}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  };

  // Events tab renderer
  const renderEventsTab = () => (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
          Our Events
        </Typography>
      </Box>
      <OurEventsSlider />
    </Container>
  );

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        sx={{
          backgroundColor: "white",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          "& .MuiTabs-indicator": {
            backgroundColor: "#00b894",
          },
          "& .Mui-selected": {
            color: "#00b894 !important",
          },
        }}
      >
        <Tab
          label="Causes"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&.Mui-selected": {
              color: "#00b894",
            },
          }}
        />
        <Tab
          label="Gallery"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&.Mui-selected": {
              color: "#00b894",
            },
          }}
        />
        <Tab
          label="Events"
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&.Mui-selected": {
              color: "#00b894",
            },
          }}
        />
      </Tabs>

      <Box sx={{ py: 2 }}>
        {activeTab === 0 && renderCausesTab()}
        {activeTab === 1 && renderGalleryTab()}
        {activeTab === 2 && renderEventsTab()}
      </Box>
      <EditCauseModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        cause={editingCause}
        onSave={saveEditedCause}
        loading={loading}
      />
    </Box>
  );
};

export default CMSPage;